import { AfterViewInit, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatRipple } from '@angular/material/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, take } from 'rxjs';
import { LoggedUserService } from '../services/logged-user.service';
import { MenuLink, PermissionType, UserInfo } from '../../shared/models';
import { ADMIN_PERMISSIONS } from '../../shared/enums/tenants-permissions-enum';
import { AuthenticationService } from '../authentication/authentication.service';
import { SideNavService } from '../services/side-nav.service';
import { MenuService } from '../services/menu.service';
import { UserService } from '../http';
import { OverlayPanelService } from '../services/overlay-panel.service';

interface Flyout {
  section: MenuLink;
  top: number;
}

interface Hint {
  text: string;
  center: number;
}

/**
 * Icon rail that never changes width. Hovering or focusing a section opens a panel beside it
 * with its pages; on phones the header toggles it as a drawer instead.
 */
@Component({
  selector: 'app-left-nav',
  standalone: true,
  imports: [MatIconModule, RouterModule, CommonModule, MatRipple],
  templateUrl: './left-nav.component.html',
  styleUrl: './left-nav.component.scss',
})
export class LeftNavComponent implements AfterViewInit {
  readonly sideNavService = inject(SideNavService);
  private readonly loggedUserService = inject(LoggedUserService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly menuService = inject(MenuService);
  private readonly userService = inject(UserService);
  private readonly overlayPanelService = inject(OverlayPanelService);
  private readonly router = inject(Router);

  private static readonly CLOSE_DELAY_MS = 220;
  private static readonly OPEN_DELAY_MS = 90;
  private static readonly PANEL_HEADER_PX = 76;
  private static readonly PANEL_ROW_PX = 38;
  private static readonly TWO_COLUMNS_FROM = 7;
  private static readonly VIEWPORT_MARGIN_PX = 12;

  readonly home: MenuLink = {
    name: 'Inicio',
    shortName: 'Inicio',
    url: '/home',
    icon: 'home',
    permission: '',
    type: 'innovaPlatform',
    sublinks: [],
  };
  readonly settings: MenuLink = {
    name: 'Configuración',
    shortName: 'Ajustes',
    url: '/admin',
    icon: 'settings',
    permission: '',
    type: 'innovaPlatform',
    sublinks: [],
  };

  userInfo: UserInfo | undefined = this.loggedUserService.loggedUserInfo;
  showConfig = true;

  readonly links = signal<MenuLink[]>(this.menuService.getNavigation());
  readonly flyout = signal<Flyout | null>(null);
  readonly hint = signal<Hint | null>(null);
  readonly currentUrl = signal(this.router.url);
  readonly drawerLinks = computed(() => this.links().filter((link) => link.type === 'tpms'));

  private openTimer?: ReturnType<typeof setTimeout>;
  private closeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
        this.closeFlyout(true);
      });
  }

  ngAfterViewInit(): void {
    this.links.update((value) => value.filter((link) => this.hasPermission(link.permission, link.type)));
    this.showConfig = this.userInfo?.user.type === 'Administrator' || this.hasAnyAdminPermission();
  }

  get expandMenu(): boolean {
    return this.sideNavService.isExpanded();
  }

  /** A section is current while any of its pages is open. */
  isActive(section: MenuLink): boolean {
    const url = this.currentUrl().split('?')[0];
    return [section.url, ...section.sublinks.map((link) => link.url)].some((link) => {
      const target = '/' + link.replace(/^\//, '').split('/')[0];
      return url === target || url.startsWith(target + '/');
    });
  }

  isCurrentPage(url: string): boolean {
    return this.currentUrl().split('?')[0] === '/' + url.replace(/^\//, '');
  }

  /** Sections with a single page get a name hint instead of a panel. */
  hasPages(section: MenuLink): boolean {
    return section.sublinks.length > 1;
  }

  twoColumns(section: MenuLink): boolean {
    return section.sublinks.length >= LeftNavComponent.TWO_COLUMNS_FROM;
  }

  openFlyout(section: MenuLink, anchor: EventTarget | null): void {
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);

    const element = anchor as HTMLElement;
    if (!this.hasPages(section)) {
      this.flyout.set(null);
      const rect = element.getBoundingClientRect();
      this.hint.set(
        section.name !== section.shortName ? { text: section.name, center: rect.top + rect.height / 2 } : null,
      );
      return;
    }

    this.hint.set(null);
    const top = this.flyoutTop(section, element);
    if (this.flyout()) {
      this.flyout.set({ section, top });
      return;
    }
    this.openTimer = setTimeout(() => this.flyout.set({ section, top }), LeftNavComponent.OPEN_DELAY_MS);
  }

  keepFlyout(): void {
    clearTimeout(this.closeTimer);
  }

  closeFlyout(now = false): void {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);

    this.hint.set(null);
    if (now) {
      this.flyout.set(null);
      return;
    }
    this.closeTimer = setTimeout(() => this.flyout.set(null), LeftNavComponent.CLOSE_DELAY_MS);
  }

  toggleMenu(forceValue?: boolean): void {
    forceValue !== undefined ? this.sideNavService.set(forceValue) : this.sideNavService.toggle();
  }

  logout(): void {
    const userInfo = this.loggedUserService.loggedUserInfo;
    this.overlayPanelService.closeAll();

    if (userInfo?.user?.isImpersonated) {
      this.userService
        .resetImpersonate()
        .pipe(take(1))
        .subscribe(() => {
          this.authenticationService.logout();
          this.toggleMenu();
        });
    } else {
      this.authenticationService.logout();
      this.toggleMenu();
    }
  }

  hasPermission(permission: string, type: PermissionType): boolean {
    if (!this.userInfo) return false;
    if (this.userInfo.user.type === 'Administrator') return true;
    if (!permission) return true;

    return this.userInfo.permissions[type]?.includes(permission) ?? false;
  }

  private hasAnyAdminPermission(): boolean {
    const permissions = this.userInfo?.permissions?.innovaPlatform ?? [];
    return ADMIN_PERMISSIONS.some((permission) => permissions.includes(permission));
  }

  private flyoutTop(section: MenuLink, anchor: HTMLElement): number {
    const count = section.sublinks.length;
    const rows = this.twoColumns(section) ? Math.ceil(count / 2) : count;
    const height = LeftNavComponent.PANEL_HEADER_PX + rows * LeftNavComponent.PANEL_ROW_PX + 16;
    const top = anchor.getBoundingClientRect().top - 6;
    const limit = window.innerHeight - height - LeftNavComponent.VIEWPORT_MARGIN_PX;

    return Math.max(LeftNavComponent.VIEWPORT_MARGIN_PX, Math.min(top, limit));
  }
}
